import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useAuthMode } from '../../hooks/useAuthMode';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';
import { useSecurityService, AuditEventType } from '../../services/securityService';
import { SessionTimeoutDialog } from './SessionTimeoutDialog';

interface SessionManagerProps {
  children: React.ReactNode;
}

export const SessionManager: React.FC<SessionManagerProps> = ({ children }) => {
  const { isAuthenticated, user, logout } = useAuthMode();
  const { logUserAction } = useSecurityService();
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  // Track whether user explicitly triggered logout (vs. unexpected auth loss)
  const intentionalLogoutRef = useRef(false);

  const timeoutMinutes = parseInt(import.meta.env.VITE_SESSION_TIMEOUT_MINUTES || '30');
  const warningMinutes = 5;

  const handleSessionWarning = useCallback(() => {
    setRemainingSeconds(warningMinutes * 60);
    setShowTimeoutWarning(true);
    
    if (user) {
      logUserAction(AuditEventType.SESSION_TIMEOUT, {
        action: 'warning_displayed',
        remainingMinutes: warningMinutes,
      });
    }
  }, [logUserAction, user, warningMinutes]);

  // On timeout: sign the user out (countdown expired without interaction)
  const handleSessionTimeout = useCallback(async () => {
    if (user) {
      logUserAction(AuditEventType.SESSION_TIMEOUT, {
        action: 'session_timeout_logout',
      });
    }
    setShowTimeoutWarning(false);
    intentionalLogoutRef.current = true;
    await logout();
  }, [logUserAction, user, logout]);

  const { resetTimer } = useSessionTimeout({
    timeoutMinutes,
    warningMinutes,
    onWarning: handleSessionWarning,
    onTimeout: handleSessionTimeout,
  });

  const handleExtendSession = useCallback(() => {
    setShowTimeoutWarning(false);
    resetTimer();
    
    if (user) {
      logUserAction(AuditEventType.LOGIN, {
        action: 'session_extended',
      });
    }
  }, [resetTimer, logUserAction, user]);

  const handleLogout = useCallback(async () => {
    intentionalLogoutRef.current = true;
    setShowTimeoutWarning(false);
    
    if (user) {
      logUserAction(AuditEventType.LOGOUT, {
        action: 'manual_logout',
      });
    }
    
    await logout();
  }, [logout, logUserAction, user]);

  // Detect unexpected auth loss — if token expires server-side, trigger logout
  const prevAuthRef = useRef(isAuthenticated);
  useEffect(() => {
    if (prevAuthRef.current && !isAuthenticated && !intentionalLogoutRef.current) {
      logUserAction(AuditEventType.SESSION_TIMEOUT, {
        action: 'unexpected_auth_loss_logout',
      });
    }
    prevAuthRef.current = isAuthenticated;
    if (!isAuthenticated) {
      intentionalLogoutRef.current = false;
    }
  }, [isAuthenticated, logUserAction]);

  // Log login events
  useEffect(() => {
    if (isAuthenticated && user) {
      logUserAction(AuditEventType.LOGIN, {
        loginMethod: 'azure_ad',
        userAgent: navigator.userAgent,
      });
    }
  }, [isAuthenticated, user, logUserAction]);

  // Update countdown for timeout dialog
  useEffect(() => {
    if (!showTimeoutWarning) return;

    const interval = setInterval(() => {
      setRemainingSeconds(prev => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(interval);
  }, [showTimeoutWarning]);

  return (
    <>
      {children}
      <SessionTimeoutDialog
        open={showTimeoutWarning}
        onExtendSession={handleExtendSession}
        onLogout={handleLogout}
        onCountdownExpired={handleSessionTimeout}
        remainingSeconds={remainingSeconds}
      />
    </>
  );
};
